import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import type { ProductScanRawConfig } from './config'
import { withProductScanDefaults } from './config'
import {
  useProductScan,
  ScanRequestError,
  type CameraUnavailableReason,
  type ProductScanResult,
} from './useProductScan'
import SplashScreen from './components/SplashScreen'
import ScannerScreen from './components/ScannerScreen'
import SuccessScreen from './components/SuccessScreen'
// @ts-expect-error SCSS modules are untyped in this project
import * as styles from './product-scan.module.scss'

type StepService = {
  post: (endpoint: string, extra?: Record<string, unknown>) => Promise<Response>
}

export type ProductScanStepProps = {
  step: {
    id?: string | number
    originalStepId?: string | number
    possibleCoinsAwarded?: number
    productScan?: ProductScanRawConfig
  }
  breakId?: string
  service: StepService
  onEngage?: () => void
  onAwardPoints?: (points?: number) => void
  nextStepButton?: React.ReactNode
  skipButton?: React.ReactNode
  isOnScreen?: boolean
}

const cameraErrorReason = (error: unknown): CameraUnavailableReason => {
  const name = (error as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return 'no-camera'
  }
  return 'dismissed'
}

const ProductScanStep = ({
  step,
  breakId,
  service,
  onEngage,
  onAwardPoints,
  nextStepButton,
  skipButton,
  isOnScreen = true,
}: ProductScanStepProps) => {
  const { t } = useTranslation('productScan')
  const streamRef = useRef<MediaStream | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  // Guards the unmount-races-permission-grant hazard below: getUserMedia can resolve after the
  // step has already unmounted (user backed out mid-prompt), and without this the just-granted
  // stream's tracks are never stopped — an orphaned camera light.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const config = useMemo(
    () =>
      withProductScanDefaults(step.productScan, {
        splashCtaText: t('splashCtaText'),
        scannerTitle: t('scannerTitle'),
        scannerHelperText: t('scannerHelperText'),
        scannerInstructions: t('scannerInstructions'),
        invalidScanPillText: t('invalidScanPillText'),
        retryCtaText: t('retryCtaText'),
        successTitle: t('successTitle'),
        successCtaText: t('successCtaText'),
        unlockLabel: t('unlockLabel'),
      }),
    [step.productScan, t],
  )

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
    setStream(null)
  }, [])

  const requestCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return { granted: false as const, reason: 'no-camera' as const }
    }
    try {
      const granted = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      if (!mountedRef.current) {
        // Unmount raced this grant — nothing left to hand the stream to, so stop it here
        // rather than leaving the camera light on until GC.
        granted.getTracks().forEach(track => track.stop())
        return { granted: false as const, reason: 'dismissed' as const }
      }
      streamRef.current = granted
      setStream(granted)
      return { granted: true as const }
    } catch (e) {
      return { granted: false as const, reason: cameraErrorReason(e) }
    }
  }, [])

  const scan = useCallback(
    async (code: string) => {
      const stepConfigId = step.originalStepId ?? step.id
      const response = await service.post('scan', { breakId, stepConfigId, code })
      // Error bodies (429/401/400/404) carry no `outcome`; without this they fell through as a match.
      if (!response.ok) throw new ScanRequestError(response.status)
      const body = await response.json()
      return body as ProductScanResult
    },
    [service, breakId, step.id, step.originalStepId],
  )

  const handleMatched = useCallback(
    () => {
      onAwardPoints?.(step.possibleCoinsAwarded || 0)
      onEngage?.()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAwardPoints, onEngage, step.possibleCoinsAwarded],
  )

  // ALREADY_SCANNED is success-shaped (same product, counted once) — reveal the next-step
  // control via onEngage but never award points a second time.
  const handleAlreadyScanned = useCallback(() => {
    onEngage?.()
  }, [onEngage])

  const handleNotMatched = useCallback(() => {}, [])

  const { state, cameraUnavailableReason, errorKind, result, startScan, submitCode, retry } = useProductScan({
    scan,
    requestCamera,
    stopCamera,
    onMatched: handleMatched,
    onAlreadyScanned: handleAlreadyScanned,
    onNotMatched: handleNotMatched,
  })

  const announcement =
    state === 'checking'
      ? t('checkingAnnouncement')
      : state === 'matched'
        ? t('matchedAnnouncement')
        : state === 'not-matched'
          ? t('notMatchedAnnouncement')
          : ''

  return (
    <div className={styles.root}>
      <div aria-live="polite" className={styles.srOnly}>
        {announcement}
      </div>

      {state === 'splash' && <SplashScreen config={config} onStart={startScan} />}

      {(state === 'requesting-camera' ||
        state === 'scanning' ||
        state === 'checking' ||
        state === 'not-matched' ||
        state === 'camera-denied') && (
        <ScannerScreen
          config={config}
          state={state}
          cameraUnavailableReason={cameraUnavailableReason}
          stream={stream}
          isOnScreen={isOnScreen}
          onDecoded={submitCode}
          onManualSubmit={submitCode}
          onRetry={retry}
        />
      )}

      {state === 'matched' && result && (
        <>
          <SuccessScreen config={config} result={result} />
          {nextStepButton}
        </>
      )}

      {state === 'error' && (
        <div className={styles.step}>
          <h2 className={styles.headline}>
            {errorKind === 'rate-limited' ? t('rateLimitedHeadline') : t('errorHeadline')}
          </h2>
          <p className={styles.introCopy}>
            {errorKind === 'rate-limited' ? t('rateLimitedBody') : t('errorBody')}
          </p>
          <button type="button" className={styles.cta} onClick={startScan}>
            {t('retryCtaText')}
          </button>
        </div>
      )}

      {/* The shell hands every step its skip control (Step3/Step4 `skipButton`, gated on the
          step's showSkipButton); this step used to drop it, so skip never showed. */}
      {skipButton && <div className={styles.skip}>{skipButton}</div>}
    </div>
  )
}

export default ProductScanStep
